import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import "../../../styles/pages/EnergyConsumptionForm.css";

const parseJSON = (val) => {
  if (!val) return null;
  try { return typeof val === "string" ? JSON.parse(val) : val; } catch { return null; }
};

const ACTIVITY_ICONS = {
  "Electricity Consumption": "⚡",
  "Diesel Consumption":      "⛽",
  "Petrol Consumption":      "⛽",
  "Water Consumption":       "💧",
  "Hazardous Waste":         "🗑️",
  "Effluent Monitoring":     "📊",
};

const shouldShowEmission = (emissionDetails, sourceDetails, sourceId) => {
  if (!emissionDetails) return false;
  if (sourceDetails && sourceId) {
    const opt = (sourceDetails.options ?? []).find((o) => String(o.option_id) === String(sourceId));
    if (opt && opt.is_emission_factor_required !== undefined && opt.is_emission_factor_required !== null)
      return Number(opt.is_emission_factor_required) === 1;
  }
  if (emissionDetails.is_emission_factor_required !== undefined && emissionDetails.is_emission_factor_required !== null)
    return Number(emissionDetails.is_emission_factor_required) === 1;
  return true;
};

const ViewField = ({ label, value, hideLabel = false, fullWidth = false }) => (
  <div className={`org-form-field ecf-view-field${fullWidth ? " ecf-view-field--full" : ""}`}>
    {!hideLabel && <span className="ecf-view-field__label">{label}</span>}
    <div className={`ecf-view-field__value${!value ? " ecf-view-field__value--empty" : ""}`}>
      {value || "—"}
    </div>
  </div>
);

const ViewParagraph = ({ label, value }) => (
  <div className="org-form-field ecf-view-field">
    <span className="ecf-view-field__label">{label}</span>
    <p className={`ecf-view-field__paragraph${!value ? " ecf-view-field__paragraph--empty" : ""}`}>
      {value || "No notes added."}
    </p>
  </div>
);

const StatusBadge = ({ status }) => {
  const mod =
    status?.toLowerCase() === "draft"     ? "draft"
  : status?.toLowerCase() === "submitted" ? "submitted"
  : status?.toLowerCase() === "approved"  ? "approved"
  : "default";
  return <span className={`ecf-status-badge ecf-status-badge--${mod}`}>{status ?? "—"}</span>;
};

const EvidenceViewer = ({ url, name }) => {
  const isImg = url ? /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url) : false;
  if (!url) return <div className="ecf-evidence--empty">N/A — No evidence uploaded</div>;
  return (
    <div className="ecf-evidence">
      {isImg && (
        <div className="ecf-evidence__preview">
          <img src={url} alt={name ?? "Evidence"} className="ecf-evidence__img"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
        </div>
      )}
      <div className="ecf-evidence__row">
        <div className="ecf-evidence__info">
          <span className="ecf-evidence__icon">{isImg ? "🖼️" : "📎"}</span>
          <span className="ecf-evidence__name">{name ?? "Evidence file"}</span>
        </div>
        <a href={url} download={name ?? true} target="_blank" rel="noopener noreferrer"
          className="ecf-evidence__download">⬇ Download</a>
      </div>
    </div>
  );
};

const blankEntry = () => ({
  _id: Math.random().toString(36).slice(2),
  value: "", source_id: "", source_name: "", disposal_id: "", disposal_name: "",
});

const blankMonitoringEntry = () => ({
  _id: Math.random().toString(36).slice(2),
  source_id: "", source_name: "", value: "", parameters: {},
});

const EMPTY_FORM = {
  record_date: "", department_id: "", site_id: "", legal_entity: "",
  energy_type_id: "", notes: "", evidence_file: null,
  parameters: {}, entries: [blankEntry()], monitoring_entries: [blankMonitoringEntry()],
};

const validate = (form, activityData) => {
  const errors = {};
  if (!String(form.record_date    ?? "").trim()) errors.record_date    = "Date is required";
  if (!String(form.site_id        ?? "").trim()) errors.site_id        = "Location is required";
  if (!String(form.legal_entity   ?? "").trim()) errors.legal_entity   = "Legal Entity is required";
  if (!String(form.energy_type_id ?? "").trim()) errors.energy_type_id = "Activity Type is required";
  if (form.record_date) {
    const today = new Date().toISOString().split("T")[0];
    if (form.record_date > today) errors.record_date = "Date cannot be a future date";
  }
  if (activityData) {
    if (activityData.data_type === "quantity") {
      form.entries?.forEach((entry, idx) => {
        if (!String(entry.value ?? "").trim())      errors[`entry_${idx}_value`]   = "Value is required";
        else if (Number(entry.value) <= 0)          errors[`entry_${idx}_value`]   = "Value must be greater than 0";
        if (activityData.requires_source) {
          const sd = parseJSON(activityData.source_field_details);
          if (sd?.is_required && !String(entry.source_id ?? "").trim())
            errors[`entry_${idx}_source`] = `${sd.field_label ?? "Source"} is required`;
        }
        if (activityData.requires_disposal_method) {
          const dd = parseJSON(activityData.disposal_field_details);
          if ((dd?.is_required === 1 || dd?.is_required === true) && !String(entry.disposal_id ?? "").trim())
            errors[`entry_${idx}_disposal`] = `${dd?.field_label ?? "Disposal Method"} is required`;
        }
      });
    }
    if (activityData.data_type === "monitoring") {
      const sd = activityData.requires_source ? parseJSON(activityData.source_field_details) : null;
      form.monitoring_entries?.forEach((entry, idx) => {
        if (sd?.is_required && !String(entry.source_id ?? "").trim())
          errors[`mon_${idx}_source`] = `${sd.field_label ?? "Source"} is required`;
        if (activityData.requires_source) {
          if (!String(entry.value ?? "").trim())    errors[`mon_${idx}_value`] = "Value is required";
          else if (Number(entry.value) <= 0)        errors[`mon_${idx}_value`] = "Value must be greater than 0";
        }
        if (activityData.requires_parameters) {
          const _vRaw = parseJSON(activityData.parameter_details);
          const params = Array.isArray(_vRaw) ? _vRaw
            : [...(Array.isArray(_vRaw?.org_parameters)    ? _vRaw.org_parameters    : []),
               ...(Array.isArray(_vRaw?.mapped_parameters) ? _vRaw.mapped_parameters : [])];
          params.filter((p) => p.status === "active").forEach((p) => {
            if (!String(entry.parameters?.[p.parameter_id] ?? "").trim())
              errors[`mon_${idx}_param_${p.parameter_id}`] = `${p.name} is required`;
          });
        }
      });
    }
  }
  return errors;
};

const resolveSource = (slot, options = []) => {
  if (!slot) return { source_id: "", source_name: "" };
  if (slot.selected_option?.option_id != null) {
    const idStr = String(slot.selected_option.option_id);
    const match = options.find((o) => String(o.option_id) === idStr);
    return { source_id: idStr, source_name: match?.option_value ?? slot.selected_option.option_value ?? "" };
  }
  if (slot.source && typeof slot.source === "object") {
    const id   = slot.source.id   != null ? String(slot.source.id)   : "";
    const name = slot.source.name ?? "";
    const byId  = id   ? options.find((o) => String(o.option_id) === id)                          : null;
    const byVal = name ? options.find((o) => o.option_value?.toLowerCase() === name.toLowerCase()) : null;
    const match = byId ?? byVal;
    return { source_id: match ? String(match.option_id) : id, source_name: match?.option_value ?? name };
  }
  if (slot.source != null && slot.source !== "") {
    const raw   = String(slot.source).trim();
    const byId  = options.find((o) => String(o.option_id) === raw);
    if (byId)  return { source_id: String(byId.option_id), source_name: byId.option_value ?? "" };
    const byVal = options.find((o) => o.option_value?.toLowerCase() === raw.toLowerCase());
    if (byVal) return { source_id: String(byVal.option_id), source_name: byVal.option_value ?? "" };
    return { source_id: "", source_name: raw };
  }
  return { source_id: "", source_name: "" };
};

const resolveDisposal = (slot, options = []) => {
  if (!slot) return { disposal_id: "", disposal_name: "" };
  if (slot.section_type === "disposal_field_details" && slot.selected_option?.option_id != null)
    return { disposal_id: String(slot.selected_option.option_id), disposal_name: slot.selected_option.option_value ?? "" };
  if (slot.disposal_field_details?.option_id != null)
    return { disposal_id: String(slot.disposal_field_details.option_id), disposal_name: slot.disposal_field_details.option_value ?? "" };
  if (slot.disposal_method != null && slot.disposal_method !== "") {
    const raw   = String(slot.disposal_method);
    const byId  = options.find((o) => String(o.option_id ?? o) === raw);
    if (byId)  return { disposal_id: String(byId.option_id ?? byId), disposal_name: byId.option_value ?? byId };
    const byVal = options.find((o) => (o.option_value ?? o)?.toLowerCase() === raw.toLowerCase());
    if (byVal) return { disposal_id: String(byVal.option_id ?? byVal), disposal_name: byVal.option_value ?? byVal };
    return { disposal_id: "", disposal_name: raw };
  }
  return { disposal_id: "", disposal_name: "" };
};

const buildEditEntries = (record, sourceOptions = [], disposalOptions = []) => {
  const dj = parseJSON(record?.data_json);
  if (!dj) return [blankEntry()];
  if (Array.isArray(dj.entries) && dj.entries.length > 0) {
    const valueSlots    = dj.entries.filter((e) =>
      e.section_type === "emission_factor_details" ||
      e.section_type === "source_field_details"   ||
      e.section_type === "value"
    );
    const disposalSlots = dj.entries.filter((e) => e.section_type === "disposal_field_details");
    if (valueSlots.length > 0) {
      return valueSlots.map((slot, i) => {
        const { source_id, source_name }     = resolveSource(slot, sourceOptions);
        const dispSlot                        = disposalSlots[i] ?? null;
        const { disposal_id, disposal_name } = dispSlot
          ? resolveDisposal(dispSlot, disposalOptions)
          : { disposal_id: "", disposal_name: "" };
        return { _id: Math.random().toString(36).slice(2), value: String(slot.entered_value ?? slot.value ?? ""), source_id, source_name, disposal_id, disposal_name };
      });
    }
    if (disposalSlots.length > 0) {
      return disposalSlots.map((slot) => {
        const { disposal_id, disposal_name } = resolveDisposal(slot, disposalOptions);
        return { _id: Math.random().toString(36).slice(2), value: String(slot.entered_value ?? slot.value ?? ""), source_id: "", source_name: "", disposal_id, disposal_name };
      });
    }
    return dj.entries.map((slot) => {
      const { source_id, source_name }     = resolveSource(slot, sourceOptions);
      const { disposal_id, disposal_name } = resolveDisposal(slot, disposalOptions);
      return { _id: Math.random().toString(36).slice(2), value: String(slot.entered_value ?? slot.value ?? ""), source_id, source_name, disposal_id, disposal_name };
    });
  }
  const { source_id, source_name }     = resolveSource(dj, sourceOptions);
  const { disposal_id, disposal_name } = resolveDisposal(dj, disposalOptions);
  return [{ _id: Math.random().toString(36).slice(2), value: String(record.consumption_value ?? ""), source_id, source_name, disposal_id, disposal_name }];
};

const buildEditMonitoringEntries = (record, sourceOptions = []) => {
  const dj = parseJSON(record?.data_json);
  if (!dj) return [blankMonitoringEntry()];

  // ── NEW flat format: entries[] with monitoring_field_details ─────────────
  const entrySlots = Array.isArray(dj.entries)
    ? dj.entries.filter((e) => e.section_type === "monitoring_field_details")
    : [];

  if (entrySlots.length > 0) {
    return entrySlots.map((slot) => {
      // The source_field_id stored on selected_option is the KEY that links
      // this entry to its parameters in dj.parameters[]
      const sourceFieldId = slot.selected_option?.source_field_id ?? null;

      // Resolve source_id — option_id may be null for text-type sources,
      // in that case we match by option_value against sourceOptions
      let source_id   = "";
      let source_name = slot.selected_option?.option_value ?? "";

      if (slot.selected_option?.option_id != null) {
        source_id = String(slot.selected_option.option_id);
        // Verify the name from our options list (more reliable)
        const match = sourceOptions.find((o) => String(o.option_id) === source_id);
        if (match) source_name = match.option_value ?? source_name;
      } else if (source_name) {
        // option_id was null — try matching by name
        const match = sourceOptions.find(
          (o) => o.option_value?.toLowerCase() === source_name.toLowerCase()
        );
        if (match) source_id = String(match.option_id);
      }

      // Pull only the parameters whose source_field_id matches this entry
      const slotParams = (dj.parameters ?? []).filter(
        (p) => p.section_type === "parameter_details"
          && (p.source_field_id === sourceFieldId
              || String(p.source_field_id) === String(sourceFieldId))
      );

      const paramMap = {};
      slotParams.forEach((p) => {
        paramMap[p.parameter_id] = p.entered_value ?? p.value ?? "";
      });

      return {
        _id:        Math.random().toString(36).slice(2),
        source_id,
        source_name,
        value:      String(slot.entered_value ?? slot.value ?? ""),
        parameters: paramMap,
      };
    });
  }

  // ── Legacy: monitoring_entries[] array ───────────────────────────────────
  if (Array.isArray(dj.monitoring_entries) && dj.monitoring_entries.length > 0) {
    return dj.monitoring_entries.map((me) => {
      const { source_id, source_name } = resolveSource(
        { selected_option: { option_id: me.source_id, option_value: me.source_name } },
        sourceOptions
      );
      const paramMap = {};
      (me.parameters ?? []).forEach((p) => {
        paramMap[p.parameter_id] = p.entered_value ?? p.value ?? "";
      });
      return {
        _id: Math.random().toString(36).slice(2),
        source_id, source_name,
        value:      String(me.value ?? me.entered_value ?? ""),
        parameters: paramMap,
      };
    });
  }

  // ── Legacy: flat source_id on root ────────────────────────────────────────
  if (dj.source_id != null || dj.source != null) {
    const { source_id, source_name } = resolveSource(dj, sourceOptions);
    const paramMap = {};
    (dj.parameters ?? []).forEach((p) => {
      paramMap[p.parameter_id] = p.entered_value ?? p.value ?? "";
    });
    return [{
      _id: Math.random().toString(36).slice(2),
      source_id, source_name,
      value:      String(dj.entered_value ?? dj.value ?? ""),
      parameters: paramMap,
    }];
  }

  return [blankMonitoringEntry()];
};

const buildEditParameters = (record) => {
  const params = parseJSON(record?.data_json)?.parameters ?? [];
  const map = {};
  params.forEach((p) => { map[p.parameter_id] = p.entered_value ?? p.value ?? ""; });
  return map;
};

// ── Remove button — inline, aligned to bottom of its grid cell ───────────────
const RemoveBtn = ({ onClick, disabled }) => (
  <div className="org-form-field" style={{ display: "flex", alignItems: "flex-end", paddingBottom: "8px" }}>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Remove Row"
      style={{
        background: "#fee2e2",
        border: "1px solid #fca5a5",
        color: "#dc2626",
        borderRadius: "6px",
        padding: "8px", // Reduced padding
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "14px",
        fontWeight: 600,
        width: "36px", // Fixed small width
        height: "36px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      ✕
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
const EnergyConsumptionForm = ({
  onClose, onSaved, editRecord,
  reportingYear:   propReportingYear,
  reportingYearId: propReportingYearId,
  isViewMode = false,
}) => {
  const navigate     = useNavigate();
  const fileInputRef = useRef(null);
  const { reportingYear: contextReportingYear } = useReportingYear();

  const [resolvedYearId,    setResolvedYearId]    = useState(propReportingYearId ?? null);
  const [resolvedYearLabel, setResolvedYearLabel] = useState(propReportingYear ?? contextReportingYear ?? "");
  const [yearLoading,       setYearLoading]       = useState(!propReportingYearId);
  const [yearLocked,        setYearLocked]        = useState(false);

  useEffect(() => {
    if (propReportingYearId) {
      setResolvedYearId(propReportingYearId);
      setResolvedYearLabel(propReportingYear ?? contextReportingYear ?? "");
      setYearLoading(false);
      return;
    }
    const fetchYearMeta = async () => {
      setYearLoading(true);
      try {
        const res   = await apiClient.post("/api/reportingyear");
        const years = res.data?.data ?? [];
        const norm  = (v) => (v ?? "").toString().trim().replace(/^fy/i, "").toLowerCase();
        let matched = contextReportingYear
          ? years.find((y) => norm(y.year_label) === norm(contextReportingYear))
          : null;
        if (!matched) matched =
          years.find((y) => y.is_current === 1 || y.is_current === true) ??
          years.find((y) => y.is_active  === 1 || y.is_active  === true) ?? null;
        if (!matched && years.length > 0) {
          const unlocked = years.filter((y) => !y.is_locked);
          matched = unlocked.sort((a, b) => b.id - a.id)[0] ?? years.sort((a, b) => b.id - a.id)[0];
        }
        setResolvedYearId(matched?.id ?? null);
        setResolvedYearLabel(matched?.year_label ?? contextReportingYear ?? "");
        setYearLocked(matched ? matched.is_locked === 1 : false);
      } catch { setResolvedYearId(null); }
      finally  { setYearLoading(false); }
    };
    fetchYearMeta();
  }, [propReportingYearId, propReportingYear, contextReportingYear]);

  const [form, setForm] = useState(() =>
    editRecord
      ? {
          record_date:        (editRecord.entry_date ?? editRecord.record_date ?? "").split("T")[0],
          department_id:      editRecord.department_id    ?? "",
          site_id:            editRecord.location_id      ?? editRecord.site_id ?? "",
          legal_entity:       editRecord.entity_name      ?? editRecord.legal_entity ?? editRecord.legal_entity_name ?? "",
          energy_type_id:     editRecord.activity_type_id ?? editRecord.energy_type_id ?? "",
          notes:              editRecord.notes            ?? "",
          evidence_file:      null,
          entries:            buildEditEntries(editRecord, [], []),
          parameters:         buildEditParameters(editRecord),
          monitoring_entries: buildEditMonitoringEntries(editRecord, []),
        }
      : { ...EMPTY_FORM, entries: [blankEntry()], parameters: {}, monitoring_entries: [blankMonitoringEntry()] }
  );

  const [errors,           setErrors]           = useState({});
  const [dropdowns,        setDropdowns]        = useState({ departments: [], unit_types: [] });
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [sites,            setSites]            = useState([]);
  const [loadingSites,     setLoadingSites]     = useState(false);
  const [activityTypes,    setActivityTypes]    = useState([]);
  const [loadingActivity,  setLoadingActivity]  = useState(false);
  const [activityData,     setActivityData]     = useState(null);
  const [loadingDetail,    setLoadingDetail]    = useState(false);
  const [status,           setStatus]           = useState("idle");
  const [apiError,         setApiError]         = useState("");
  const [evidenceFileName, setEvidenceFileName] = useState("");
  const [divisor,          setDivisor]          = useState(1000);

  const [siteDisplayName,       setSiteDisplayName]       = useState("");
  const [departmentDisplayName, setDepartmentDisplayName] = useState("");
  const [activityDisplayName,   setActivityDisplayName]   = useState("");

  useEffect(() => {
    apiClient.get("/api/energy-fiscal-period-list")
      .then((r) => {
        const d = r.data?.data ?? {};
        setDropdowns({ departments: d.departments?.data ?? [], unit_types: d.unit_types?.data ?? [] });
      })
      .catch(() => {})
      .finally(() => setLoadingDropdowns(false));
  }, []);

  useEffect(() => {
    if (!resolvedYearId) return;
    setLoadingSites(true);
    apiClient.get("/api/site-list-yearbased", { params: { reporting_year_id: resolvedYearId } })
      .then((r) => setSites(r.data?.data ?? []))
      .catch(() => setSites([]))
      .finally(() => setLoadingSites(false));
  }, [resolvedYearId]);

  useEffect(() => {
    if (!sites.length || !form.site_id) return;
    const site = sites.find((s) => String(s.site_id) === String(form.site_id));
    if (site) setSiteDisplayName(site.site_name ?? "");
  }, [sites, form.site_id]);

  useEffect(() => {
    if (!dropdowns.departments.length || !form.department_id) return;
    const dept = dropdowns.departments.find((d) => String(d.department_id) === String(form.department_id));
    if (dept) setDepartmentDisplayName(dept.department_name ?? "");
  }, [dropdowns.departments, form.department_id]);

  useEffect(() => {
    if (!activityTypes.length || !form.energy_type_id) return;
    const act = activityTypes.find((a) => String(a.id) === String(form.energy_type_id));
    if (act) setActivityDisplayName(act.name ?? "");
  }, [activityTypes, form.energy_type_id]);

  useEffect(() => {
    if (!editRecord || !sites.length) return;
    setForm((prev) => {
      if (prev.legal_entity) return prev;
      const site   = sites.find((s) => String(s.site_id) === String(prev.site_id));
      const entity = site?.legal_entity ?? site?.entity_name ?? site?.legal_entity_name ?? "";
      return entity ? { ...prev, legal_entity: entity } : prev;
    });
  }, [sites, editRecord]);

  useEffect(() => {
    if (!resolvedYearId) return;
    setLoadingActivity(true);
    apiClient.get("/api/activitytype", { params: { reporting_year_id: resolvedYearId } })
      .then((r) => setActivityTypes(r.data?.data ?? []))
      .catch(() => setActivityTypes([]))
      .finally(() => setLoadingActivity(false));
  }, [resolvedYearId]);

  useEffect(() => {
    if (!form.energy_type_id) { setActivityData(null); return; }
    setLoadingDetail(true);
    apiClient.get("/api/get-activity-data", { params: { activitytype_id: form.energy_type_id } })
      .then((r) => {
        const d = Number(r.data?.divideby);
        if (d && d > 0) setDivisor(d);
        const data = r.data?.data?.[0] ?? null;
        setActivityData(data);
        if (editRecord && data) {
          const sourceOptions   = parseJSON(data.source_field_details)?.options  ?? [];
          const disposalOptions = parseJSON(data.disposal_field_details)?.options ?? [];
          if (data.data_type === "quantity") {
            setForm((prev) => ({ ...prev, entries: buildEditEntries(editRecord, sourceOptions, disposalOptions) }));
          } else if (data.data_type === "monitoring") {
            setForm((prev) => ({ ...prev, monitoring_entries: buildEditMonitoringEntries(editRecord, sourceOptions) }));
          }
        }
      })
      .catch(() => setActivityData(null))
      .finally(() => setLoadingDetail(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.energy_type_id]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const emissionDetails      = activityData?.is_emission_applicable   ? parseJSON(activityData.emission_factor_details) : null;
  const sourceDetails        = activityData?.requires_source          ? parseJSON(activityData.source_field_details)    : null;
  const disposalFieldDetails = activityData?.requires_disposal_method ? parseJSON(activityData.disposal_field_details)  : null;

  const parameterDetails = (() => {
    if (!activityData?.requires_parameters) return null;
    const parsed = parseJSON(activityData.parameter_details);
    if (!parsed) return null;
    if (Array.isArray(parsed)) return parsed;
    const merged = [
      ...(Array.isArray(parsed.org_parameters)    ? parsed.org_parameters    : []),
      ...(Array.isArray(parsed.mapped_parameters) ? parsed.mapped_parameters : []),
    ];
    return merged.length > 0 ? merged : null;
  })();

  const hasEmission = !!emissionDetails;

  const maxEntries = (() => {
    let max = Infinity;
    if (sourceDetails?.field_type === "dropdown") {
      const n = (sourceDetails.options ?? []).filter((o) => o.status === "active").length;
      if (n > 0) max = Math.min(max, n);
    }
    if (disposalFieldDetails) {
      const n = (disposalFieldDetails.options ?? []).filter((o) => o.status === "active").length;
      if (n > 0) max = Math.min(max, n);
    }
    return max;
  })();

  const maxMonitoringEntries = (() => {
    if (!sourceDetails) return Infinity;
    const n = (sourceDetails.options ?? []).filter((o) => o.status === "active").length;
    return n > 0 ? n : Infinity;
  })();

  const computeEntryEmission = (val, sourceId) => {
    if (!emissionDetails) return "0.00";
    if (!shouldShowEmission(emissionDetails, sourceDetails, sourceId)) return "0.00";
    if (!val || Number(val) <= 0) return "0.00";
    const factor = Number(emissionDetails.factor_value) || 0;
    return ((Number(val) * factor) / (divisor || 1000)).toFixed(2);
  };

  const activityIcon = activityData ? (ACTIVITY_ICONS[activityData.name] ?? "📋") : "";

  const buildDataJson = () => {
    const json = {};
    if (activityData?.data_type === "quantity") {
      const flatEntries = [];
      (form.entries ?? []).forEach((e) => {
        const entryShowsEmission = shouldShowEmission(emissionDetails, sourceDetails, e.source_id);
        if (activityData.requires_source) {
          const slot = {
            section_type:  entryShowsEmission ? "emission_factor_details" : "source_field_details",
            entered_value: Number(e.value) || 0,
            unit:          activityData.unit_name ?? "",
          };
          if (e.source_id) slot.selected_option = {
            source_field_id: sourceDetails?.field_id ?? null,
            option_id:       Number(e.source_id),
            option_value:    e.source_name,
          };
          if (entryShowsEmission && emissionDetails) {
            slot.emission_factor      = Number(emissionDetails.factor_value);
            slot.emission_factor_unit = emissionDetails.emission_factor_unit ?? "";
            slot.estimated_emission   = Number(computeEntryEmission(e.value, e.source_id));
          }
          flatEntries.push(slot);
        } else if (!activityData.requires_disposal_method) {
          const slot = { section_type: "value", entered_value: Number(e.value) || 0, unit: activityData.unit_name ?? "" };
          if (entryShowsEmission && emissionDetails) {
            slot.emission_factor      = Number(emissionDetails.factor_value);
            slot.emission_factor_unit = emissionDetails.emission_factor_unit ?? "";
            slot.estimated_emission   = Number(computeEntryEmission(e.value, e.source_id));
          }
          flatEntries.push(slot);
        }
        if (activityData.requires_disposal_method && e.disposal_id) {
          flatEntries.push({
            section_type:    "disposal_field_details",
            selected_option: { source_field_id: disposalFieldDetails?.field_id ?? null, option_id: Number(e.disposal_id), option_value: e.disposal_name },
            entered_value:   Number(e.value) || 0,
            unit:            activityData.unit_name ?? "",
          });
        }
        if (!activityData.requires_source && activityData.requires_disposal_method && !e.disposal_id)
          flatEntries.push({ section_type: "value", entered_value: Number(e.value) || 0, unit: activityData.unit_name ?? "" });
      });
      json.entries = flatEntries;
    }
    if (activityData?.data_type === "monitoring") {
          const _raw = parseJSON(activityData.parameter_details);
          const params = !_raw ? [] : Array.isArray(_raw) ? _raw
            : [...(Array.isArray(_raw?.org_parameters) ? _raw.org_parameters : []),
              ...(Array.isArray(_raw?.mapped_parameters) ? _raw.mapped_parameters : [])];
          const activeParams = params.filter((p) => p.status === "active");

          // Source options — we need each option's own field_id (not the parent field_id)
          // The API returns options with their own option_id; the source_field_id saved
          // per-entry must be that specific option's field_id so parameters can be linked back.
          const sourceOptions = sourceDetails?.options ?? [];

          const flatEntries    = [];
          const flatParameters = [];

          (form.monitoring_entries ?? []).forEach((me) => {
            // ── Resolve the source_field_id for THIS specific entry ──────────────
            // Priority:
            //   1. Look up the selected option in sourceDetails — use its field_id if present
            //   2. Fall back to the parent sourceDetails.field_id
            //   3. Fall back to null
            const selectedOpt   = me.source_id
              ? sourceOptions.find((o) => String(o.option_id) === String(me.source_id))
              : null;
            const sourceFieldId = selectedOpt?.field_id        // per-option field_id
              ?? selectedOpt?.source_field_id                  // some APIs put it here
              ?? sourceDetails?.field_id                       // parent field_id fallback
              ?? null;

            // ── Entry slot ────────────────────────────────────────────────────────
            flatEntries.push({
              section_type: "monitoring_field_details",
              selected_option: {
                source_field_id: sourceFieldId,
                option_id:       me.source_id ? Number(me.source_id) : null,
                option_value:    me.source_name ?? "",
              },
              entered_value: Number(me.value) || 0,
              unit:          activityData.unit_name ?? "",
            });

            // ── Parameters for this entry, tagged with the same source_field_id ──
            activeParams.forEach((p) => {
              flatParameters.push({
                section_type:    "parameter_details",
                parameter_id:    p.parameter_id,
                name:            p.name,
                entered_value:   Number(me.parameters?.[p.parameter_id]) || 0,
                unit_id:         p.unit_id,
                source_field_id: sourceFieldId,  // ← links this param back to its entry
              });
            });
          });

          json.entries    = flatEntries;
          json.parameters = flatParameters;
        }
    return json;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    setApiError("");
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field])     setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (status !== "idle") setStatus("idle");
  };

  const handleBlur = (field) => () => {
    const fe = validate(form, activityData);
    if (fe[field]) setErrors((prev) => ({ ...prev, [field]: fe[field] }));
  };

  const handleSiteChange = (e) => {
    const id   = e.target.value;
    const site = sites.find((s) => String(s.site_id) === String(id));
    setForm((prev) => ({
      ...prev, site_id: id,
      ...(!editRecord && { legal_entity: site?.legal_entity ?? site?.entity_name ?? site?.legal_entity_name ?? "" }),
    }));
    setApiError("");
    if (errors.site_id)      setErrors((prev) => ({ ...prev, site_id: undefined }));
    if (errors.legal_entity) setErrors((prev) => ({ ...prev, legal_entity: undefined }));
  };

  const handleActivityChange = (e) => {
    setForm((prev) => ({ ...prev, energy_type_id: e.target.value, entries: [blankEntry()], parameters: {}, monitoring_entries: [blankMonitoringEntry()] }));
    setActivityData(null);
    if (errors.energy_type_id) setErrors((prev) => ({ ...prev, energy_type_id: undefined }));
    setApiError("");
    setStatus("idle");
  };

  const handleEntryChange = (idx, field) => (e) => {
    setForm((prev) => {
      const entries = [...prev.entries];
      entries[idx]  = { ...entries[idx], [field]: e.target.value };
      return { ...prev, entries };
    });
    const ek = field === "value" ? `entry_${idx}_value` : field.startsWith("source") ? `entry_${idx}_source` : `entry_${idx}_disposal`;
    if (errors[ek]) setErrors((prev) => ({ ...prev, [ek]: undefined }));
  };

  const handleSourceDropdownChange = (idx, options) => (e) => {
    const selectedId = e.target.value;
    const opt        = options.find((o) => String(o.option_id) === selectedId);
    setForm((prev) => {
      const entries = [...prev.entries];
      entries[idx]  = { ...entries[idx], source_id: selectedId, source_name: opt?.option_value ?? "" };
      return { ...prev, entries };
    });
    if (errors[`entry_${idx}_source`]) setErrors((prev) => ({ ...prev, [`entry_${idx}_source`]: undefined }));
  };

  const handleDisposalDropdownChange = (idx, options) => (e) => {
    const selectedId = e.target.value;
    const opt        = options.find((o) => String(o.option_id) === selectedId);
    setForm((prev) => {
      const entries = [...prev.entries];
      entries[idx]  = { ...entries[idx], disposal_id: selectedId, disposal_name: opt?.option_value ?? "" };
      return { ...prev, entries };
    });
    if (errors[`entry_${idx}_disposal`]) setErrors((prev) => ({ ...prev, [`entry_${idx}_disposal`]: undefined }));
  };

  const addEntry    = () => setForm((prev) => prev.entries.length >= maxEntries ? prev : { ...prev, entries: [...prev.entries, blankEntry()] });
  const removeEntry = (idx) => setForm((prev) => ({ ...prev, entries: prev.entries.filter((_, i) => i !== idx) }));

  const handleMonitoringSourceChange = (idx, options) => (e) => {
    const selectedId = e.target.value;
    const opt        = options.find((o) => String(o.option_id) === selectedId);
    setForm((prev) => {
      const monitoring_entries = [...prev.monitoring_entries];
      monitoring_entries[idx]  = { ...monitoring_entries[idx], source_id: selectedId, source_name: opt?.option_value ?? "" };
      return { ...prev, monitoring_entries };
    });
    if (errors[`mon_${idx}_source`]) setErrors((prev) => ({ ...prev, [`mon_${idx}_source`]: undefined }));
  };

  const handleMonitoringValueChange = (idx) => (e) => {
    setForm((prev) => {
      const monitoring_entries = [...prev.monitoring_entries];
      monitoring_entries[idx]  = { ...monitoring_entries[idx], value: e.target.value };
      return { ...prev, monitoring_entries };
    });
    if (errors[`mon_${idx}_value`]) setErrors((prev) => ({ ...prev, [`mon_${idx}_value`]: undefined }));
  };

  const handleMonitoringParamChange = (idx, pid) => (e) => {
    setForm((prev) => {
      const monitoring_entries = [...prev.monitoring_entries];
      monitoring_entries[idx]  = { ...monitoring_entries[idx], parameters: { ...monitoring_entries[idx].parameters, [pid]: e.target.value } };
      return { ...prev, monitoring_entries };
    });
    if (errors[`mon_${idx}_param_${pid}`]) setErrors((prev) => ({ ...prev, [`mon_${idx}_param_${pid}`]: undefined }));
  };

  const addMonitoringEntry    = () => setForm((prev) => prev.monitoring_entries.length >= maxMonitoringEntries ? prev : { ...prev, monitoring_entries: [...prev.monitoring_entries, blankMonitoringEntry()] });
  const removeMonitoringEntry = (idx) => setForm((prev) => ({ ...prev, monitoring_entries: prev.monitoring_entries.filter((_, i) => i !== idx) }));

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    setForm((prev) => ({ ...prev, evidence_file: file }));
    setEvidenceFileName(file?.name ?? "");
  };

  const removeFile = () => {
    setForm((prev) => ({ ...prev, evidence_file: null }));
    setEvidenceFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (saveAsDraft = false) => {
    const fieldErrors = validate(form, activityData);
    if (Object.keys(fieldErrors).length > 0) { setErrors(fieldErrors); return; }
    const dataJson  = buildDataJson();
    const entryDate = form.record_date;
    const month     = entryDate ? String(new Date(entryDate).getMonth() + 1).padStart(2, "0") : "";
    let totalValue    = 0;
    let emissionValue = "0";
    if (activityData?.data_type === "monitoring") {
      totalValue    = (form.monitoring_entries ?? []).reduce((acc, e) => acc + (Number(e.value) || 0), 0);
      emissionValue = "0";
    } else {
      totalValue = (form.entries ?? []).reduce((acc, e) => acc + (Number(e.value) || 0), 0);
      if (emissionDetails) {
        emissionValue = String(
          (form.entries ?? []).reduce((acc, e) => {
            const entryEmission = shouldShowEmission(emissionDetails, sourceDetails, e.source_id)
              ? (Number(e.value || 0) * Number(emissionDetails.factor_value)) / (divisor || 1000)
              : 0;
            return acc + entryEmission;
          }, 0)
        );
      }
    }
    const isEditMode = !!editRecord;
    const fd = new FormData();
    fd.append("reporting_year_id", resolvedYearId ?? "");
    fd.append("location_id",       form.site_id);
    fd.append("activity_type_id",  form.energy_type_id);
    fd.append("entry_date",        entryDate);
    fd.append("month",             month);
    fd.append("consumption_value", String(totalValue));
    fd.append("unit",              activityData?.unit_name ?? "");
    fd.append("emission_value",    emissionValue);
    fd.append("notes",             form.notes ?? "");
    fd.append("data_json",         JSON.stringify(dataJson));
    fd.append("status",            saveAsDraft ? "draft" : "submitted");
    fd.append("is_draft",          saveAsDraft ? "1" : "0");
    if (form.department_id)           fd.append("department_id",    form.department_id);
    if (form.legal_entity)            fd.append("legal_entity",     form.legal_entity);
    if (form.evidence_file)           fd.append("evidence_file",    form.evidence_file);
    if (isEditMode && editRecord?.id) fd.append("activity_data_id", editRecord.id);
    setStatus("loading");
    setApiError("");
    try {
      const response = await apiClient.post(
        isEditMode ? "/api/update-activity-data" : "/api/save-activity-data",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (onSaved) { onSaved(response, isEditMode); }
      else         { navigate("/dashboard/energy-consumption"); }
    } catch (err) {
      setApiError(err.response?.data?.message || err.message || "Failed to save.");
      setStatus("error");
    }
  };

  const isLoading    = status === "loading";
  const isEdit       = !!editRecord && !isViewMode;
  const isStandalone = !onClose;

  const savedEvidenceUrl  = editRecord?.evidence_file_url  ?? editRecord?.evidence_url  ?? null;
  const savedEvidenceName = editRecord?.evidence_file_name ?? editRecord?.evidence_name ?? null;

  const formattedDate = form.record_date
    ? new Date(form.record_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  if (isStandalone && yearLoading)
    return <div className="dash-page"><div className="le-empty"><div className="le-empty-text">Loading…</div></div></div>;
  if (isStandalone && yearLocked)
    return <div className="dash-page"><div className="le-empty"><div className="le-empty-text">The selected reporting year <strong>{resolvedYearLabel}</strong> is locked.</div></div></div>;
  if (isStandalone && !resolvedYearId)
    return <div className="dash-page"><div className="le-empty"><div className="le-empty-text">Please select a reporting year from the top bar to continue.</div></div></div>;

  const recordStatus = editRecord?.status ?? null;

  // ── Column counts ─────────────────────────────────────────────────────────
  const hasSource   = !!sourceDetails;
  const hasDisposal = !!(activityData?.requires_disposal_method && disposalFieldDetails);

  // +1 for the Remove button column when in edit mode with multiple entries
  // The Remove column is always included in editColCount so the grid is stable;
  // the cell itself renders empty (zero-width) when there's only 1 row.
  const baseEditCols = (hasSource ? 1 : 0) + (hasDisposal ? 1 : 0) + 1 /* value */ + 1 /* unit */ + (hasEmission ? 2 : 0);
  const editColCount = baseEditCols + 1; // +1 for Remove column (always reserved)
  const viewColCount = (hasSource ? 1 : 0) + (hasDisposal ? 1 : 0) + 1 + (hasEmission ? 2 : 0);

  // Monitoring helpers
  const monitoringSourceLabel    = sourceDetails?.field_label ?? "Source";
  const monitoringSourceRequired = sourceDetails?.is_required ?? false;
  const monitoringSourceOptions  = (sourceDetails?.options ?? []).filter((o) => o.status === "active");
  const activeParams             = (parameterDetails ?? []).filter((p) => p.status === "active");

  // +1 for Remove column (always reserved in edit mode)
  const monRow1ColsEdit = (sourceDetails ? 3 : 0) + 1; // source + value + unit + remove
  const monRow1ColsView = sourceDetails ? 2 : 0;
  const monRow2Cols     = activeParams.length;

  return (
    <div className="dash-page">

      {/* ── Page header ── */}
      <div className="le-page-header">
        <div>
          <div className="dash-page-subtitle">ENVIRONMENT &amp; CARBON</div>
          <div className="dash-page-title">
            {isViewMode ? "View Entry" : isEdit ? "Edit Entry" : "Record Activity"}
          </div>
          <div className="dash-page-subtitle">
            {isViewMode
              ? "Read-only view of this activity record."
              : "Record energy, fuel, water, waste, and monitoring data from one screen."}
          </div>
        </div>
        <div className="ecf-header-right">
          {isViewMode && recordStatus && <StatusBadge status={recordStatus} />}
          {onClose && (
            <button className="org-btn-cancel le-add-btn" onClick={onClose} disabled={isLoading}>
              ← Back to Records
            </button>
          )}
        </div>
      </div>

      {/* ── Card 1: Entry Details ── */}
      <div className="le-card ecf-card">
        <div className="org-form-section-title ecf-section-title">+ Entry Details</div>
        {isViewMode ? (
          <>
            <div className="org-form-row ecf-grid-3">
              <ViewField label="Reporting Year" value={resolvedYearLabel} />
              <ViewField label="Location"       value={siteDisplayName || editRecord?.location_name} />
              <ViewField label="Legal Entity"   value={form.legal_entity} />
            </div>
            <div className="org-form-row ecf-grid-3 ecf-row-gap">
              <ViewField label="Department"    value={departmentDisplayName || (!form.department_id ? "Not specified" : undefined)} />
              <ViewField label="Date"          value={formattedDate} />
              <ViewField label="Activity Type" value={activityDisplayName || editRecord?.activity_type} />
            </div>
          </>
        ) : (
          <>
            <div className="org-form-row ecf-grid-3">
              <div className="org-form-field">
                <div className="ecf-field-label-row">
                  <label className="org-field-label">Reporting Year <span className="req">*</span></label>
                  <span className="ecf-label-tag">From Config</span>
                </div>
                <input type="text" className="org-field-input ecf-input-disabled" value={resolvedYearLabel ?? ""} disabled />
              </div>
              <div className="org-form-field">
                <label className="org-field-label">Location <span className="req">*</span></label>
                <select className={`org-field-input${errors.site_id ? " org-field-input--error" : ""}`}
                  value={form.site_id} onChange={handleSiteChange} onBlur={handleBlur("site_id")} disabled={loadingSites || isLoading}>
                  <option value="">{loadingSites ? "Loading…" : "Select location"}</option>
                  {sites.map((s) => <option key={s.site_id} value={s.site_id}>{s.site_name}</option>)}
                </select>
                {errors.site_id && <span className="org-field-error">{errors.site_id}</span>}
              </div>
              <div className="org-form-field">
                <label className="org-field-label">Legal Entity <span className="req">*</span></label>
                <input className={`org-field-input ecf-input-disabled${errors.legal_entity ? " org-field-input--error" : ""}`}
                  type="text" value={form.legal_entity} disabled placeholder={loadingSites ? "Loading…" : "Select a location first"} />
                {errors.legal_entity && <span className="org-field-error">{errors.legal_entity}</span>}
              </div>
            </div>
            <div className="org-form-row ecf-grid-3 ecf-row-gap">
              <div className="org-form-field">
                <div className="ecf-field-label-row">
                  <label className="org-field-label">Department</label>
                  <span className="ecf-label-tag">Optional</span>
                </div>
                <select className="org-field-input" value={form.department_id}
                  onChange={handleChange("department_id")} disabled={loadingDropdowns || isLoading}>
                  <option value="">{loadingDropdowns ? "Loading…" : "Select Department"}</option>
                  {dropdowns.departments.map((d) => <option key={d.department_id} value={d.department_id}>{d.department_name}</option>)}
                </select>
              </div>
              <div className="org-form-field">
                <label className="org-field-label">Date <span className="req">*</span></label>
                <input className={`org-field-input${errors.record_date ? " org-field-input--error" : ""}`}
                  type="date" value={form.record_date} onChange={handleChange("record_date")}
                  onBlur={handleBlur("record_date")} disabled={isLoading} max={new Date().toISOString().split("T")[0]} />
                {errors.record_date && <span className="org-field-error">{errors.record_date}</span>}
              </div>
              <div className="org-form-field">
                <label className="org-field-label">Activity Type <span className="req">*</span></label>
                <select className={`org-field-input${errors.energy_type_id ? " org-field-input--error" : ""}`}
                  value={form.energy_type_id} onChange={handleActivityChange}
                  onBlur={handleBlur("energy_type_id")} disabled={loadingActivity || isLoading}>
                  <option value="">{loadingActivity ? "Loading…" : "Select activity"}</option>
                  {activityTypes.map((et) => <option key={et.id} value={et.id}>{et.name}</option>)}
                </select>
                {errors.energy_type_id && <span className="org-field-error">{errors.energy_type_id}</span>}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Card 2: Activity Details ── */}
      {form.energy_type_id && (
        <div className="le-card ecf-card ecf-detail-card">
          {loadingDetail ? (
            <div className="ecf-loading">Loading activity details…</div>
          ) : activityData && (
            <>
              <div className="org-form-section-title ecf-section-title">
                {activityIcon} {activityData.name} Details
              </div>

              {/* ══ QUANTITY ══════════════════════════════════════════════ */}
              {activityData.data_type === "quantity" && (
                <div className="ecf-entries-stack">

                  {isViewMode && form.entries.length > 0 && (
                    <div className="ecf-table-header" style={{ gridTemplateColumns: `repeat(${viewColCount}, 1fr)` }}>
                      {hasSource   && <div>{sourceDetails.field_label ?? "Source"}</div>}
                      {hasDisposal && <div>{disposalFieldDetails.field_label ?? "Disposal Method"}</div>}
                      <div>Value</div>
                      {hasEmission && <div>Emission Factor</div>}
                      {hasEmission && <div>Estimated Emission</div>}
                    </div>
                  )}

                  {form.entries.map((entry, idx) => {
                    const entryEmission      = computeEntryEmission(entry.value, entry.source_id);
                    const entryShowsEmission = shouldShowEmission(emissionDetails, sourceDetails, entry.source_id);
                    const usedSourceIds      = new Set(form.entries.filter((_, i) => i !== idx).map((e) => e.source_id).filter(Boolean));
                    const usedDisposalIds    = new Set(form.entries.filter((_, i) => i !== idx).map((e) => e.disposal_id).filter(Boolean));
                    const colCount           = isViewMode ? viewColCount : editColCount;

                    return (
                      <div key={entry._id} className="ecf-entry-row ecf-entry-row--boxed">
                       <div className="org-form-row" style={{ 
                          display: "grid", 
                          gridTemplateColumns: isViewMode ? `repeat(${viewColCount}, 1fr)` : `repeat(${baseEditCols}, 1fr) 45px`, 
                          gap: "12px" 
                        }}>

                          {/* SOURCE */}
                          {hasSource && (
                            isViewMode ? (
                              <ViewField key="src" hideLabel label={sourceDetails.field_label ?? "Source"} value={entry.source_name || entry.source_id} />
                            ) : (
                              <div className="org-form-field" key="src">
                                <label className="org-field-label">
                                  {sourceDetails.field_label ?? "Source"}
                                  {sourceDetails.is_required ? <span className="req"> *</span> : <span className="ecf-optional"> (Optional)</span>}
                                </label>
                                {sourceDetails.field_type === "dropdown" ? (
                                  <select
                                    className={`org-field-input${errors[`entry_${idx}_source`] ? " org-field-input--error" : ""}`}
                                    value={entry.source_id != null ? String(entry.source_id) : ""}
                                    onChange={handleSourceDropdownChange(idx, (sourceDetails.options ?? []).filter((o) => o.status === "active"))}
                                    disabled={isLoading}>
                                    <option value="">Select {sourceDetails.field_label}</option>
                                    {(sourceDetails.options ?? []).filter((o) => o.status === "active")
                                      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
                                      .filter((o) => !usedSourceIds.has(String(o.option_id)))
                                      .map((o) => <option key={o.option_id} value={String(o.option_id)}>{o.option_value}</option>)}
                                  </select>
                                ) : (
                                  <input className={`org-field-input${errors[`entry_${idx}_source`] ? " org-field-input--error" : ""}`}
                                    type="text" placeholder={`Enter ${sourceDetails.field_label ?? "source"}`}
                                    value={entry.source_name} onChange={(e) => handleEntryChange(idx, "source_name")(e)} disabled={isLoading} />
                                )}
                                {errors[`entry_${idx}_source`] && <span className="org-field-error">{errors[`entry_${idx}_source`]}</span>}
                              </div>
                            )
                          )}

                          {/* DISPOSAL */}
                          {hasDisposal && (
                            isViewMode ? (
                              <ViewField key="disp" hideLabel label={disposalFieldDetails.field_label ?? "Disposal Method"} value={entry.disposal_name || entry.disposal_id} />
                            ) : (
                              <div className="org-form-field" key="disp">
                                <label className="org-field-label">
                                  {disposalFieldDetails.field_label ?? "Disposal Method"}
                                  {disposalFieldDetails.is_required === 1 || disposalFieldDetails.is_required === true ? <span className="req"> *</span> : <span className="ecf-optional"> (Optional)</span>}
                                </label>
                                <select
                                  className={`org-field-input${errors[`entry_${idx}_disposal`] ? " org-field-input--error" : ""}`}
                                  value={entry.disposal_id}
                                  onChange={handleDisposalDropdownChange(idx, (disposalFieldDetails.options ?? []).filter((o) => o.status === "active").filter((o) => !usedDisposalIds.has(String(o.option_id))))}
                                  disabled={isLoading}>
                                  <option value="">Select {disposalFieldDetails.field_label ?? "Disposal Method"}</option>
                                  {(disposalFieldDetails.options ?? []).filter((o) => o.status === "active")
                                    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
                                    .filter((o) => !usedDisposalIds.has(String(o.option_id)))
                                    .map((o) => <option key={o.option_id} value={String(o.option_id)}>{o.option_value}</option>)}
                                </select>
                                {errors[`entry_${idx}_disposal`] && <span className="org-field-error">{errors[`entry_${idx}_disposal`]}</span>}
                              </div>
                            )
                          )}

                          {/* VALUE */}
                          {isViewMode ? (
                            <ViewField key="val" hideLabel label="Value"
                              value={entry.value != null && entry.value !== "" ? `${parseFloat(entry.value).toLocaleString()} ${activityData.unit_name ?? ""}`.trim() : "—"} />
                          ) : (
                            <div className="org-form-field" key="val">
                              <label className="org-field-label">Value <span className="req">*</span></label>
                              <input className={`org-field-input${errors[`entry_${idx}_value`] ? " org-field-input--error" : ""}`}
                                type="number" placeholder="e.g. 500" min={0}
                                value={entry.value} onChange={handleEntryChange(idx, "value")} disabled={isLoading} />
                              {errors[`entry_${idx}_value`] && <span className="org-field-error">{errors[`entry_${idx}_value`]}</span>}
                            </div>
                          )}

                          {/* UNIT (edit only) */}
                          {!isViewMode && (
                            <div className="org-form-field" key="unit">
                              <label className="org-field-label">Unit</label>
                              <input className="org-field-input ecf-input-disabled" type="text" value={activityData.unit_name ?? "—"} disabled />
                            </div>
                          )}

                          {/* EMISSION FACTOR */}
                         {hasEmission && (
                          <div className="org-form-field">
                            <label className="org-field-label">Emission Factor</label>
                            <input 
                              className="org-field-input ecf-input-disabled" 
                              type="text" 
                              value={entryShowsEmission
                                ? `${emissionDetails.factor_value ?? "0"} ${emissionDetails.emission_factor_unit ?? ""}`.trim()
                                : `0 ${emissionDetails.emission_factor_unit ?? ""}`.trim()} 
                              disabled 
                            />
                          </div>
                        )}

                          {/* ESTIMATED EMISSION */}
                          {hasEmission && (
                            <div className="org-form-field">
                              <label className="org-field-label">Estimated Emission</label>
                              <input 
                                className="org-field-input ecf-input-disabled" 
                                type="text" 
                                value={entryShowsEmission ? `${entryEmission} tCO2e` : "0.00 tCO2e"} 
                                disabled 
                              />
                            </div>
                          )}

                          {/* ── REMOVE BUTTON — last column, edit mode only ── */}
                          {/* Shows when there are 2+ entries (all rows including first) */}
                          {/* Renders an invisible placeholder when only 1 entry exists  */}
                          {/* so the grid column count stays stable                      */}
                          {!isViewMode && (
                            form.entries.length > 1
                              ? <RemoveBtn onClick={() => removeEntry(idx)} disabled={isLoading} />
                              : <div className="org-form-field" /> /* empty placeholder */
                          )}

                        </div>
                      </div>
                    );
                  })}

                  {!isViewMode && form.entries.length < maxEntries && (
                    <div>
                      <button type="button" className="ecf-btn-add-more" onClick={addEntry} disabled={isLoading}>
                        + Add More
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ══ MONITORING ════════════════════════════════════════════ */}
              {activityData.data_type === "monitoring" && (
                <div className="ecf-entries-stack">
                  {form.monitoring_entries.map((me, idx) => {
                    const usedSourceIds = new Set(form.monitoring_entries.filter((_, i) => i !== idx).map((e) => e.source_id).filter(Boolean));

                    return (
                      <div key={me._id} className="ecf-entry-row ecf-entry-row--boxed">

                        {/* Row 1: Source | Value | Unit | Remove */}
                        {sourceDetails && (
                          <div className="org-form-row"
                            style={{ gridTemplateColumns: isViewMode
                              ? `repeat(${monRow1ColsView}, 1fr)`
                              : `repeat(${monRow1ColsEdit}, 1fr)` }}>

                            {/* Sample Point */}
                            {isViewMode ? (
                              <ViewField label={monitoringSourceLabel} value={me.source_name || me.source_id || "—"} />
                            ) : (
                              <div className="org-form-field">
                                <label className="org-field-label">
                                  {monitoringSourceLabel}
                                  {monitoringSourceRequired ? <span className="req"> *</span> : <span className="ecf-optional"> (Optional)</span>}
                                </label>
                                <select
                                  className={`org-field-input${errors[`mon_${idx}_source`] ? " org-field-input--error" : ""}`}
                                  value={me.source_id != null ? String(me.source_id) : ""}
                                  onChange={handleMonitoringSourceChange(idx, monitoringSourceOptions)}
                                  disabled={isLoading}>
                                  <option value="">Select {monitoringSourceLabel}</option>
                                  {monitoringSourceOptions
                                    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
                                    .filter((o) => !usedSourceIds.has(String(o.option_id)))
                                    .map((o) => <option key={o.option_id} value={String(o.option_id)}>{o.option_value}</option>)}
                                </select>
                                {errors[`mon_${idx}_source`] && <span className="org-field-error">{errors[`mon_${idx}_source`]}</span>}
                              </div>
                            )}

                            {/* Value */}
                            {isViewMode ? (
                              <ViewField label="Value"
                                value={me.value != null && me.value !== "" ? `${parseFloat(me.value).toLocaleString()} ${activityData.unit_name ?? ""}`.trim() : "—"} />
                            ) : (
                              <div className="org-form-field">
                                <label className="org-field-label">Value <span className="req">*</span></label>
                                <input
                                  className={`org-field-input${errors[`mon_${idx}_value`] ? " org-field-input--error" : ""}`}
                                  type="number" placeholder="e.g. 500" min={0}
                                  value={me.value} onChange={handleMonitoringValueChange(idx)} disabled={isLoading} />
                                {errors[`mon_${idx}_value`] && <span className="org-field-error">{errors[`mon_${idx}_value`]}</span>}
                              </div>
                            )}

                            {/* Unit (edit only) */}
                            {!isViewMode && (
                              <div className="org-form-field">
                                <label className="org-field-label">Unit</label>
                                <input className="org-field-input ecf-input-disabled" type="text" value={activityData.unit_name ?? "—"} disabled />
                              </div>
                            )}

                            {/* ── REMOVE BUTTON — last column of row 1, edit mode only ── */}
                            {!isViewMode && (
                              form.monitoring_entries.length > 1
                                ? <RemoveBtn onClick={() => removeMonitoringEntry(idx)} disabled={isLoading} />
                                : <div className="org-form-field" /> /* empty placeholder */
                            )}
                          </div>
                        )}

                        {/* Row 2: Parameters */}
                        {monRow2Cols > 0 && (
                          <div className="org-form-row ecf-row-gap" style={{ gridTemplateColumns: `repeat(${monRow2Cols}, 1fr)` }}>
                            {activeParams.map((p) => {
                              const val        = me.parameters?.[p.parameter_id] ?? "";
                              const numVal     = Number(val);
                              const outOfRange = val !== "" && (
                                (p.min_limit != null && numVal < p.min_limit) ||
                                (p.max_limit != null && numVal > p.max_limit)
                              );
                              
                            const unitName = p.unit_name ?? dropdowns.unit_types.find((u) => u.unit_id === p.unit_id)?.unit_name ?? "";
                              const rangePlaceholder = `${p.min_limit ?? ""}–${p.max_limit ?? ""}`;
                              if (isViewMode) {
                                return <ViewField key={p.parameter_id} label={`${p.name}${unitName ? ` (${unitName})` : ""}`} value={val !== "" ? String(val) : "—"} />;
                              }
                              return (
                                <div className="org-form-field" key={p.parameter_id}>
                                  <label className="org-field-label">{p.name}{unitName ? ` (${unitName})` : ""}</label>
                                  <input
                                    className={`org-field-input${errors[`mon_${idx}_param_${p.parameter_id}`] || outOfRange ? " org-field-input--error" : ""}`}
                                    type="number" placeholder={'Enter Value'}
                                    value={val} onChange={handleMonitoringParamChange(idx, p.parameter_id)}
                                    disabled={isLoading} min={0} />
                                  {errors[`mon_${idx}_param_${p.parameter_id}`] && <span className="org-field-error">{errors[`mon_${idx}_param_${p.parameter_id}`]}</span>}
                                  {(() => {
                                    const hasMin = p.min_limit != null;
                                    const hasMax = p.max_limit != null;
                                    const rangeText = hasMin && hasMax
                                      ? `Acceptable Range: ${p.min_limit} – ${p.max_limit}`
                                      : hasMax && !hasMin
                                      ? `Acceptable Range: < ${p.max_limit}`
                                      : hasMin && !hasMax
                                      ? `Acceptable Range: > ${p.min_limit}`
                                      : null;
                                    if (!rangeText) return null;
                                    return (
                                      <span className={`ecf-range-hint${outOfRange && !errors[`mon_${idx}_param_${p.parameter_id}`] ? " ecf-range-hint--warn" : ""}`}>
                                        {outOfRange && !errors[`mon_${idx}_param_${p.parameter_id}`] ? "⚠ " : ""}{rangeText}
                                      </span>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {!isViewMode && form.monitoring_entries.length < maxMonitoringEntries && (
                    <div>
                      <button type="button" className="ecf-btn-add-more" onClick={addMonitoringEntry} disabled={isLoading}>
                        + Add More
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Notes + Evidence ── */}
          <div className="org-form-row ecf-grid-2 ecf-notes-row">
            {isViewMode
              ? <ViewParagraph label="Notes" value={form.notes} />
              : (
                <div className="org-form-field">
                  <div className="ecf-field-header">
                    <label className="org-field-label">Notes</label>
                    <span className="ecf-optional">Optional</span>
                  </div>
                  <textarea className="org-field-input ecf-textarea" rows={4} maxLength={1000}
                    placeholder="Add any context, assumptions, or remarks..."
                    value={form.notes} onChange={handleChange("notes")} disabled={isLoading} />
                  <div className="ecf-char-count">{(form.notes ?? "").length}/1000</div>
                </div>
              )}
            <div className="org-form-field">
              <div className="ecf-field-header">
                <label className="org-field-label">Evidence</label>
                {!isViewMode && <span className="ecf-optional">Optional</span>}
              </div>
              {isViewMode ? (
                <EvidenceViewer url={savedEvidenceUrl} name={savedEvidenceName} />
              ) : !evidenceFileName ? (
                <div className="ecf-upload-zone" onClick={() => fileInputRef.current?.click()}>
                  <div className="ecf-upload-zone__icon">⬆️</div>
                  <div className="ecf-upload-zone__title">Upload File</div>
                  <div className="ecf-upload-zone__hint">PDF, JPG, PNG, XLSX · Max 10MB</div>
                </div>
              ) : (
                <div className="ecf-file-chip">
                  <div className="ecf-file-chip__info">
                    <span className="ecf-file-chip__icon">📎</span>
                    <span className="ecf-file-chip__name">{evidenceFileName}</span>
                  </div>
                  <button type="button" className="ecf-file-chip__remove" onClick={removeFile}>✕</button>
                </div>
              )}
              {!isViewMode && (
                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx" className="ecf-file-input" onChange={handleFileChange} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      {isViewMode ? (
        <div className="ecf-footer ecf-footer--view">
          {onClose && <button className="org-btn-cancel" onClick={onClose}>← Back to Records</button>}
        </div>
      ) : (
        <div className="ecf-footer">
          {apiError && <span className="org-save-error">✕ {apiError}</span>}
          <button className="org-btn-cancel" onClick={() => handleSubmit(true)} disabled={loadingDropdowns || loadingDetail || isLoading}>
            💾 Save as Draft
          </button>
          <button className="org-btn-save" onClick={() => handleSubmit(false)} disabled={loadingDropdowns || loadingDetail || isLoading}>
            {isLoading ? "Saving…" : isEdit ? "✈ Update" : "✈ Submit"}
          </button>
        </div>
      )}

    </div>
  );
};

export default EnergyConsumptionForm;